/**
 * Each book language's hyphenation files and minimums (decision 33: per-language patterns, lazy-loaded). The files
 * are hyph-utf8's, unchanged, in `packages/layout/hyphenation/` (`SOURCES.md` there says where each came from and
 * under which licence); the minimums are the ones hyph-utf8 gives each language for typesetting.
 */
import type { BookLanguage } from '../theme/metadata.ts';
import { frozen } from '../theme/record.ts';

/** Where a language's patterns are and how close to a word's edges a break may come. */
export interface HyphenationFiles {
  /** The pattern file's name in `packages/layout/hyphenation/`. */
  readonly patterns: string;
  /** The exception file's name, or null for a language without one. */
  readonly exceptions: string | null;
  /** Fewest letters before a break. */
  readonly left: number;
  /** Fewest letters after a break. */
  readonly right: number;
}

const files = (tag: string, exceptions: boolean, right: number): HyphenationFiles => ({
  patterns: `hyph-${tag}.pat.txt`,
  exceptions: exceptions ? `hyph-${tag}.hyp.txt` : null,
  left: 2,
  right,
});

/**
 * The files for each book language, or null for a language hyphenated by the writer's soft hyphens alone. Czech has
 * none: hyph-utf8's Czech patterns are under the GPL (part 13 log, open questions). Norwegian Nynorsk shares the
 * Bokmål pattern file (hyph-utf8's two files are the same Norwegian patterns) with its own exceptions.
 */
export const HYPHENATION_FILES: Readonly<Record<BookLanguage, HyphenationFiles | null>> = frozen({
  'en-US': files('en-us', true, 3),
  'en-GB': files('en-gb', true, 3),
  de: files('de-1996', false, 2),
  fr: files('fr', false, 2),
  es: files('es', false, 2),
  it: files('it', false, 2),
  pt: files('pt', true, 3),
  nl: files('nl', true, 2),
  sv: files('sv', false, 2),
  nb: files('nb', true, 2),
  nn: { patterns: 'hyph-nb.pat.txt', exceptions: 'hyph-nn.hyp.txt', left: 2, right: 2 },
  da: files('da', false, 2),
  fi: files('fi', false, 2),
  pl: files('pl', true, 2),
  cs: null,
  ca: files('ca', true, 2),
});
